import { ref, computed } from "vue";
import { debounce } from "@/utils/debounce";

/**
 * @param {Object} options
 * @param {Object} options.props - Component props (isDraft, save, saveStatus)
 * @param {Function} options.emit - Component emit function
 * @param {Function} options.isAnyMenuOpen - Returns true if any dropdown menu is open
 */
export function useEditorSave({ props, emit, isAnyMenuOpen }) {
  const localValue = ref("");
  const lastSavedValue = ref("");
  const hasBeenFocused = ref(false);

  const statusLabel = computed(() => {
    switch (props.saveStatus) {
      case "saving": return "Saving...";
      case "saved": return "Auto Saved";
      case "error": return "Failed";
      default: return "";
    }
  });

  const handleFocus = () => {
    hasBeenFocused.value = true;
  };

  const runSave = async () => {
    if (!props.isDraft) return;
    if (!hasBeenFocused.value) return;
    if (!localValue.value || localValue.value.trim() === "") return;
    if (localValue.value === lastSavedValue.value) return;
    try {
      emit("save", localValue.value);
      if (typeof props.save === "function") {
        await props.save(localValue.value);
      }
      lastSavedValue.value = localValue.value;
    } catch (error) {
      console.error("Failed to save encounter note", error);
    }
  };

  const debouncedSave = debounce(() => runSave(), 2000);

  const scheduleSave = () => {
    if (isAnyMenuOpen()) return;
    debouncedSave();
  };

  const handleBlur = () => {
    debouncedSave.cancel();
    if (isAnyMenuOpen()) return;
    runSave();
  };

  /**
   * Strip action buttons from HTML content when in read-only mode
   */
  const stripActionButtonsFromHTML = (html) => {
    if (!html || props.isDraft) return html;
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    const actionElements = tempDiv.querySelectorAll('.card-action-actions');
    actionElements.forEach((el) => el.remove());
    return tempDiv.innerHTML;
  };

  /** Initialize localValue and lastSavedValue from modelValue */
  const initValue = (modelValue) => {
    const cleaned = stripActionButtonsFromHTML(modelValue) || "";
    localValue.value = cleaned;
    lastSavedValue.value = cleaned;
  };

  return {
    localValue,
    lastSavedValue,
    statusLabel,
    runSave,
    debouncedSave,
    scheduleSave,
    handleBlur,
    stripActionButtonsFromHTML,
    initValue,
    handleFocus,
  };
}
